import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigationMenu } from '@/hooks/useNavigationMenu';
import { normalizePath, resolvePageNavigation } from '@/lib/menuNavigation';

type RecentPage = { id: string; title: string; href: string; pinned: boolean; visitedAt: number };
const limit = (pages: RecentPage[]) => [
  ...pages.filter((page) => page.pinned).sort((a, b) => b.visitedAt - a.visitedAt),
  ...pages.filter((page) => !page.pinned).sort((a, b) => b.visitedAt - a.visitedAt).slice(0, 10),
];

function readPages(key: string): RecentPage[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '[]');
    if (!Array.isArray(parsed)) return [];
    const unique = new Map<string, RecentPage>();
    for (const page of parsed) {
      if (typeof page?.href !== 'string' || !page.href.startsWith('/') || page.href.startsWith('//') || typeof page.title !== 'string') continue;
      unique.set(page.href, { id: page.href, href: page.href, title: page.title, pinned: page.pinned === true, visitedAt: Number(page.visitedAt) || 0 });
    }
    return limit([...unique.values()]);
  } catch {
    return [];
  }
}

export function useRecentPages() {
  const { user } = useAuth();
  const location = useLocation();
  const menu = useNavigationMenu();
  const currentPath = normalizePath(location.pathname);
  const navigation = resolvePageNavigation(menu, currentPath);
  const key = `industrializacao-recent-pages:${user?.id ?? 'guest'}`;
  const [pages, setPages] = useState(() => readPages(key));
  const lastVisit = useRef('');

  useEffect(() => {
    if (!navigation || lastVisit.current === currentPath) return;
    lastVisit.current = currentPath;
    setPages((previous) => {
      const pinned = previous.find((page) => page.id === currentPath)?.pinned ?? false;
      return limit([{ id: currentPath, href: currentPath, title: navigation.title, pinned, visitedAt: Date.now() }, ...previous.filter((page) => page.id !== currentPath)]);
    });
  }, [currentPath, navigation?.title]);

  const permittedPages = useMemo(() => pages.filter((page) => resolvePageNavigation(menu, page.href)), [pages, menu]);
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(permittedPages)); } catch { /* Preferências disponíveis nesta sessão. */ }
  }, [key, permittedPages]);

  const togglePin = useCallback((id: string) => setPages((previous) => limit(previous.map((page) => page.id === id ? { ...page, pinned: !page.pinned } : page))), []);
  const removePage = useCallback((id: string) => setPages((previous) => previous.filter((page) => page.id !== id || page.pinned)), []);
  return { pages: permittedPages, currentPath, togglePin, removePage };
}
